import { useEffect, useState } from 'react'

import dayjs from 'dayjs'

import { useCallMutation } from '@/common/api/client'
import {
  BEHAVIOR_LOG_SEARCH_CONDITION_CLEAR,
  convertConditionToBehaviorLogsVars,
  useBehaviorLogSearchCondition,
  useBehaviorLogsApi,
  usePageRankingsApi
} from '@/common/api/modules/behaviorLog'
import { useSiteGetApi } from '@/common/api/modules/site'
import {
  PageRankingEntry,
  PageRankingPeriod,
  PageRankingsAnalysisResponse,
  PageRankingsAnalysisVars,
  PageRankingsApiPath
} from '@/common/api/params/pageRankings'
import { isSameSearchCondition, useFirstLoading } from '@/common/api/search'
import { useSiteId } from '@/common/app/site'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import GroupButton from '@/components/view/atoms/button/GroupButton'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import LoadingObject, { LoadingObjectType } from '@/components/view/atoms/loading/LoadingObject'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import NoResultGuidance from '@/components/view/molecules/search/NoResultGuidance'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import BehaviorListTable from '@/components/container/modules/recommend/behavior/table/BehaviorListTable'
import BehaviorSearchConditionField from '@/components/container/modules/recommend/behavior/table/BehaviorSearchConditionField'
import TitlePageInfo from '@/components/container/utils/pager/TitlePageInfo'

const PERIOD_OPTIONS = [
  { value: 'daily', label: '日別' },
  { value: 'weekly', label: '週別' },
  { value: 'monthly', label: '月別' }
]

const formatPageUrl = (url: string, siteDomain: string | undefined): string => {
  if (!siteDomain) return url
  try {
    const siteOrigin = new URL(siteDomain).origin
    const pageUrl = new URL(url)
    if (pageUrl.origin === siteOrigin) {
      return `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}` || '/'
    }
  } catch {
    // fall back to original
  }
  return url
}

const formatDwell = (ms: number | null | undefined): string => {
  if (ms == null) return '-'
  if (ms < 1000) return `${ms}ms`
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}秒`
  const min = Math.floor(sec / 60)
  const remSec = sec % 60
  return remSec > 0 ? `${min}分${remSec}秒` : `${min}分`
}

const RankChange = ({ change }: { change: number | null }) => {
  if (change === null || change === undefined) {
    return (
      <Badge badgeType={BadgeType.DEFAULT} size={10}>
        NEW
      </Badge>
    )
  }
  if (change > 0)
    return (
      <ItemAlign gapX={2} align='center'>
        <Text color={TextColor.SUCCESS} size={12}>
          {change}UP
        </Text>
        <Icon type={IconType.INCREASE} size={16} color={TextColor.SUCCESS} />
      </ItemAlign>
    )
  if (change < 0)
    return (
      <ItemAlign gapX={2} align='center'>
        <Text color={TextColor.CRITICAL} size={12}>
          {Math.abs(change)}DOWN
        </Text>
        <Icon type={IconType.DECREASE} size={16} color={TextColor.CRITICAL} />
      </ItemAlign>
    )
  return (
    <ItemAlign gapX={2} align='center'>
      <Text color={TextColor.SECONDARY} size={12}>
        STAY
      </Text>
      <Icon type={IconType.ARROW_FORWARD} size={16} color={TextColor.SECONDARY} />
    </ItemAlign>
  )
}

const RankingCard = ({
  ranking,
  rankPeriod,
  siteDomain
}: {
  ranking: PageRankingPeriod
  rankPeriod: 'daily' | 'weekly' | 'monthly'
  siteDomain: string | undefined
}) => (
  <Area bottomMargin={16}>
    <Heading>
      {rankPeriod === 'daily'
        ? `${dayjs(ranking.period_start).format('YYYY年MM月DD日')}`
        : `${dayjs(ranking.period_start).format('YYYY年MM月DD日')}〜${dayjs(ranking.period_end).format('YYYY年MM月DD日')}`}
    </Heading>
    {ranking.pages.length === 0 ? (
      <AreaContent>
        <Text color={TextColor.SECONDARY} align='center'>
          データなし
        </Text>
      </AreaContent>
    ) : (
      <div className='flex flex-col'>
        {ranking.pages.map((page: PageRankingEntry) => (
          <div key={page.rank} className='border-primary flex flex-col gap-2 border-b p-4'>
            <ItemAlign gapX={2} align='center'>
              <Text color={TextColor.CAUTION} size={16} weight='bold'>
                {page.rank}
              </Text>
              <div className='ml-4'>
                <RankChange change={page.rank_change} />
              </div>
            </ItemAlign>
            <a
              href={page.page_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-link block truncate text-xs hover:underline'
            >
              {formatPageUrl(page.page_url, siteDomain)}
            </a>
            <ItemAlign>
              <ItemAlign gapX={2}>
                <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
                  PV
                </Badge>
                <Text>{page.pv.toLocaleString()}</Text>
              </ItemAlign>
              <ItemAlign gapX={2}>
                <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
                  UU
                </Badge>
                <Text>{page.uu.toLocaleString()}</Text>
              </ItemAlign>
              <ItemAlign gapX={2}>
                <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
                  平均滞在
                </Badge>
                <Text>{formatDwell(page.avg_dwell_ms)}</Text>
              </ItemAlign>
            </ItemAlign>
          </div>
        ))}
      </div>
    )}
  </Area>
)

const BehaviorListPage = () => {
  const siteId = useSiteId()
  const [activeTab, setActiveTab] = useState<'logs' | 'rankings'>('logs')
  const [rankPeriod, setRankPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  const [analysisText, setAnalysisText] = useState<string | null>(null)

  const [condition, setCondition, onUpdatePage] = useBehaviorLogSearchCondition()
  const isDefault = isSameSearchCondition(condition, BEHAVIOR_LOG_SEARCH_CONDITION_CLEAR)

  const { data: logsData, isLoading: isLogLoading } = useBehaviorLogsApi(
    activeTab === 'logs' ? convertConditionToBehaviorLogsVars(siteId, condition) : null
  )
  const isFirstLoading = useFirstLoading(isLogLoading)

  const { data: rankingsData, isLoading: rankingsLoading } = usePageRankingsApi(
    activeTab === 'rankings' ? { site_id: siteId, period: rankPeriod, limit: 20, periods: 3 } : null
  )

  const { data: siteData } = useSiteGetApi(
    activeTab === 'rankings' ? { site_id: siteId } : undefined
  )

  const { trigger: triggerAnalysis, isMutating: isAnalyzing } = useCallMutation<
    PageRankingsAnalysisResponse,
    PageRankingsAnalysisVars
  >(PageRankingsApiPath.PAGE_RANKINGS_ANALYSIS, {
    onSuccess: (data) => setAnalysisText(data.analysis),
    onError: () => setAnalysisText('分析の取得に失敗しました'),
    errorNotShown: true
  })

  useEffect(() => {
    setAnalysisText(null)
  }, [rankPeriod])

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='行動履歴'
        badgeNode={<AppTitleBadge type='recommend' />}
        description='タグから取得したユーザー行動データの一覧'
        behindNode={
          activeTab === 'logs' && logsData ? (
            <TitlePageInfo
              page={condition.page}
              perPage={condition.perPage}
              total={logsData.total}
              unit='件'
            />
          ) : undefined
        }
        rightNode={
          <>
            {activeTab === 'rankings' && (
              <AiButton
                onClick={() =>
                  triggerAnalysis({
                    site_id: siteId,
                    rankings: rankingsData ? rankingsData.rankings : [],
                    period_type: rankPeriod
                  })
                }
                loading={isAnalyzing}
                disabled={!rankingsData}
                size='large'
              >
                AI解析を実行する
              </AiButton>
            )}
          </>
        }
      />

      <Content>
        <Area noBorder>
          <TabList border>
            <Tab selected={activeTab === 'logs'} onClick={() => setActiveTab('logs')}>
              行動ログ
            </Tab>
            <Tab selected={activeTab === 'rankings'} onClick={() => setActiveTab('rankings')}>
              ページランキング
            </Tab>
          </TabList>
        </Area>
        {activeTab === 'logs' && (
          <>
            {isFirstLoading ? (
              <PageListLoading />
            ) : (
              <Area tableWrap noBorder>
                <BehaviorSearchConditionField condition={condition} setCondition={setCondition} />

                {isLogLoading ? (
                  <PageListSearching />
                ) : logsData?.total ? (
                  <BehaviorListTable
                    logs={logsData.logs}
                    total={logsData.total}
                    condition={condition}
                    setCondition={setCondition}
                    onUpdatePage={onUpdatePage}
                  />
                ) : (
                  <NoResultGuidance
                    searched={!isDefault}
                    onClickReset={
                      !isDefault
                        ? () => setCondition(BEHAVIOR_LOG_SEARCH_CONDITION_CLEAR)
                        : undefined
                    }
                  />
                )}
              </Area>
            )}
          </>
        )}
        {activeTab === 'rankings' && (
          <>
            <div>
              <GroupButton
                choices={PERIOD_OPTIONS.map((p) => ({ value: p.value, label: p.label }))}
                value={rankPeriod}
                onSelect={(val) => setRankPeriod(val as 'daily' | 'weekly' | 'monthly')}
              />
            </div>

            {rankingsLoading ? (
              <PageListLoading />
            ) : rankingsData?.rankings && rankingsData.rankings.length > 0 ? (
              <>
                {(isAnalyzing || analysisText) && (
                  <div className='flex flex-col gap-2'>
                    <Text size={16} lineHeight={150} weight='bold' tag='h2'>
                      AI ランキング分析
                    </Text>
                    <AiInsideArea border>
                      {isAnalyzing && (
                        <div className='flex flex-col gap-4 p-4'>
                          <LoadingObject type={LoadingObjectType.TEXT} />
                          <LoadingObject type={LoadingObjectType.TEXT} />
                          <LoadingObject type={LoadingObjectType.TEXT} />
                          <LoadingObject type={LoadingObjectType.TEXT} />
                          <LoadingObject type={LoadingObjectType.TEXT} />
                          <LoadingObject type={LoadingObjectType.TEXT} />
                          <LoadingObject type={LoadingObjectType.TEXT} />
                          <LoadingObject type={LoadingObjectType.TEXT} />
                        </div>
                      )}
                      {analysisText && (
                        <AreaContent>
                          {analysisText.split('\n').map((line, i) => (
                            <Text
                              key={i}
                              size={14}
                              weight={line.startsWith('■') ? 'bold' : 'normal'}
                              tag='p'
                            >
                              {line}
                            </Text>
                          ))}
                        </AreaContent>
                      )}
                    </AiInsideArea>
                  </div>
                )}
                <div className='grid grid-cols-3 gap-4'>
                  {rankingsData.rankings.map((ranking, idx) => (
                    <RankingCard
                      key={idx}
                      ranking={ranking}
                      rankPeriod={rankPeriod}
                      siteDomain={siteData?.domain}
                    />
                  ))}
                </div>
              </>
            ) : (
              <Area noBorder>
                <div className='py-10 text-center'>
                  <Text color={TextColor.SECONDARY}>
                    ランキングデータがありません。行動履歴データが蓄積されると、ページURLごとのアクセスランキングが表示されます。
                  </Text>
                </div>
              </Area>
            )}
          </>
        )}
      </Content>
    </Main>
  )
}

export default BehaviorListPage
